import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import type { DiningSpecialRequestsSection_booking$key } from "./__generated__/DiningSpecialRequestsSection_booking.graphql";
import { DiningSpecialRequestsModal } from "./components/DiningSpecialRequestsModal/DiningSpecialRequestsModal";
import { SpecialRequestSection } from "./components/SpecialRequestSection/SpecialRequestSection";
import { normalizeSpecialRequestTags } from "./components/utils";

type Props = {
  bookingRef: DiningSpecialRequestsSection_booking$key | null;
};

export const DiningSpecialRequestsSection = ({ bookingRef }: Props) => {
  const booking = useFragment(DiningSpecialRequestsSectionFragment, bookingRef);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const bookingData = booking as any;
  const ownerClient = bookingData?.ownerClient;
  const ownerId =
    ownerClient?.__typename === "Member" ||
    ownerClient?.__typename === "Customer"
      ? ownerClient?.id ?? null
      : null;

  const otherPatronIds =
    bookingData?.rounds?.edges
      ?.map((edge: any) => edge?.node?.player?.id)
      .filter(
        (id: string | null | undefined): id is string =>
          Boolean(id) && (!ownerId || id !== ownerId)
      ) ?? [];

  if (!booking) {
    return null;
  }

  const dietaryTags = normalizeSpecialRequestTags(booking.dietaryTags);
  const occasionTags = normalizeSpecialRequestTags(booking.occasionTags);
  const otherRequestLabel = booking.notesFromMember?.trim() || t`None`;

  return (
    <Box padding={5} gap={5} backgroundColor="surface-primary">
      <HStack justifyContent="space-between" alignItems="center">
        <Text variant="mainHeading">{t`Special Requests`}</Text>
      </HStack>

      <VStack gap={4}>
        <SpecialRequestSection
          title={t`Does your party have any allergies?`}
          content={
            dietaryTags.length > 0 ? (
              <Box flexDirection="row" flexWrap="wrap">
                {dietaryTags.map((tag) => (
                  <Box key={tag} marginRight={2} marginBottom={2}>
                    <TagPill label={tag} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Text variant="mainBaseSemiBold" color="text-secondary">
                {t`None`}
              </Text>
            )
          }
        />

        <SpecialRequestSection
          title={t`Are you celebrating a special occasion?`}
          content={
            occasionTags.length > 0 ? (
              <Box flexDirection="row" flexWrap="wrap">
                {occasionTags.map((tag) => (
                  <Box key={tag} marginRight={2} marginBottom={2}>
                    <TagPill label={tag} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Text variant="mainBaseMedium" color="text-secondary">
                {t`None`}
              </Text>
            )
          }
        />

        <SpecialRequestSection
          title={t`Other special requests?`}
          content={
            <Text variant="mainBaseMedium" color="text-secondary">
              {otherRequestLabel}
            </Text>
          }
        />
      </VStack>

      {booking.canUpdate ? (
        <Button
          variant="outline"
          width="100%"
          accessibilityLabel={t`Edit special requests`}
          onPress={() => setIsModalOpen(true)}
        >
          {t`Edit Special Requests`}
        </Button>
      ) : null}
      {booking ? (
        <DiningSpecialRequestsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          bookingRef={booking}
          ownerId={ownerId}
          otherPatronIds={otherPatronIds}
        />
      ) : null}
    </Box>
  );
};

const DiningSpecialRequestsSectionFragment = graphql`
  fragment DiningSpecialRequestsSection_booking on Booking {
    ...DiningSpecialRequestsModal_booking
    ... on DiningBooking {
      id
      canUpdate
      dietaryTags
      occasionTags
      notesFromMember
      startSlot {
        agenda {
          facility {
            diningDietaryTags
            diningOccasionTags
          }
        }
      }
      ownerClient {
        __typename
        ... on Member {
          id
        }
        ... on Customer {
          id
        }
      }
      rounds {
        edges {
          node {
            player {
              id
            }
          }
        }
      }
    }
  }
`;

export { DiningSpecialRequestsSectionFragment };

const TagPill = ({ label }: { label: string }) => (
  <Box
    backgroundColor="surface-main-primary-subtlest"
    borderRadius="4"
    paddingHorizontal={3}
    paddingVertical={1}
  >
    <Text variant="mainTiny">{label}</Text>
  </Box>
);
